import type { ReactNode } from "react";
import { cn } from "@wa/ui";
import { IconBtn } from "./Btn";
import { fmtNum } from "./format";

/**
 * A word that switches something on: the filter pill of the CRM's toolbars.
 *
 * `crm-cb` with `is-on` is the same "this is switched on" signal the folder
 * rails and the row menus use, and `aria-pressed` says it to a screen reader,
 * so the pill reads as a toggle rather than as a button that does something
 * once. The count sits inside the pill (`crm-cb-n`), quieter than the word.
 *
 * It exists because the prospect tabs had three private copies of exactly this
 * markup (the audits desk, the call queue and the follow-up queue's "late
 * only"), each a hand-written `<button>` with the same four classes. A fourth
 * copy is how the sizes start to drift: the lead filters are nine pills wide,
 * and one of them half a pixel taller than its neighbours is visible.
 *
 * Single-select or multi-select is the caller's business: this draws one pill
 * and reports a click. A row of them where exactly one is on is still a filter
 * row, not a tab strip - `PillTabs` is for the latter.
 */
export function ToggleChip({
  active,
  onClick,
  children,
  count,
  disabled,
  className,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  /** What the filter would find, printed inside the pill. Left out, no count. */
  count?: number;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <IconBtn
      small
      wide
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(active && "is-on", className)}
    >
      {children}
      {count != null ? <span className="crm-cb-n">{fmtNum(count)}</span> : null}
    </IconBtn>
  );
}
